import arrayMove from "array-move";
import { produce } from "immer";
import React from "react";

import { doNothing } from "@examspark/common";
import { questionTypesList } from "@examspark/common/dist/meta/questions";
import {
  Course,
  ExamConfigData,
  Objective,
  Resource,
  SearchableResourceFilterInput,
  TagInput,
  Unit,
} from "@examspark/types";

export type ExamEditorObjective = Objective & {
  enabled?: boolean;
  additionalFilter?: SearchableResourceFilterInput;
};

export type ExamEditorState = {
  name: string;
  isInitialized?: boolean;
  // todo: refactor these somehow to be specific to the bonfire app
  banks?: string[];
  config: ExamConfigData;
  courses?: Course[];
  description: string;
  difficulty?: string;
  grade?: string;
  interactionTypes: string[];
  language?: string;
  maturity?: string;
  objectiveAuthorityIds?: string[];
  objectives: ExamEditorObjective[];
  passages: Resource[];
  passagesAvailableCount: number;
  questionQty?: number;
  questions: Resource[];
  subject?: string;
  type?: string;
  units?: Unit[];
  tags?: TagInput[];
};

export const defaultExamEditorState: ExamEditorState = {
  config: {},
  description: "",
  interactionTypes: questionTypesList,
  name: "",
  objectives: [],
  passages: [],
  passagesAvailableCount: 0,
  questionQty: 10,
  questions: [],
};

export const sampleExamEditorState: ExamEditorState = {
  name: "Sample Exam",
  description: "This is the description",
  questions: [],
  config: {},
  interactionTypes: ["choice"],
  passages: [],
  passagesAvailableCount: 0,
  objectives: [
    {
      content:
        "use a problem-solving model that incorporates analyzing given information, formulating a plan or strategy, determining a solution, justifying the solution, and evaluating the problem-solving process and the reasonableness of the solution;",
      abbreviation: "6.1.B",
      sequence: 3,
      id: "828b94e0-0520-505a-8983-6f45611827cc",
      sk: "standard",
      type: "OBJECTIVE",
    },
    {
      content:
        "communicate mathematical ideas, reasoning, and their implications using multiple representations, including symbols, diagrams, graphs, and language as appropriate;",
      abbreviation: "6.1.D",
      sequence: 5,
      id: "a74a97f4-2960-57f3-ab89-96472c266118",
      sk: "standard",
      type: "OBJECTIVE",
    },
  ],
};

export type ExamEditorActions =
  | {
      type: "UPDATE";
      data: ExamEditorState;
    }
  | {
      type: "PRODUCE_UPDATE";
      producer: (draft: ExamEditorState) => void;
    }
  | {
      type: "SET_BANKS";
      data: string[];
    }
  | {
      type: "TOGGLE_OBJECTIVE";
      objective: ExamEditorObjective;
      enabled?: boolean;
    }
  | {
      type: "ADD_OBJECTIVES";
      objectives: ExamEditorObjective[];
    }
  | {
      type: "REMOVE_OBJECTIVES";
      objectives: ExamEditorObjective[];
    }
  | {
      type: "CLEAR_OBJECTIVES";
    }
  | {
      type: "TOGGLE_ALL_PASSAGE_ITEMS";
      enabled?: boolean;
    }
  | {
      type: "TOGGLE_ONLY_PASSAGE_ITEMS";
      enabled?: boolean;
    }
  | {
      type: "REMOVE_ITEM_AT_INDEX";
      index: number;
    }
  | {
      type: "REMOVE_ITEMS_AT_INDEX";
      indexes: number[];
    }
  | {
      type: "SET_PASSAGES_AVAILABLE_COUNT";
      count: number;
    }
  | {
      type: "ADD_ITEMS";
      questions: Resource[];
      index?: number;
    }
  | {
      type: "REPLACE_ITEM_AT_INDEX";
      index: number;
      questions: Resource[];
    }
  | {
      type: "REORDER_ITEMS";
      oldIndex: number;
      newIndex: number;
    }
  | {
      type: "RESET";
    };

export const ExamEditorContext = React.createContext<
  [ExamEditorState, React.Dispatch<ExamEditorActions>]
>([defaultExamEditorState, doNothing]);

export const examEditorReducer = (
  state: ExamEditorState,
  action: ExamEditorActions,
) => {
  // console.log(action);
  switch (action.type) {
    case "UPDATE":
      return action.data;
    case "PRODUCE_UPDATE":
      return produce(state, action.producer);
    case "RESET":
      return { ...defaultExamEditorState };
    case "ADD_ITEMS":
      for (const item of action.questions) {
        if (!state.questions.find((q) => q.id === item.id)) {
          if (action.index == null) {
            state.questions.push(item);
          } else {
            state.questions.splice(action.index, 0, item);
          }
        }
      }
      break;
    case "REPLACE_ITEM_AT_INDEX":
      state.questions.splice(action.index, 1, ...action.questions);
      break;
    case "REORDER_ITEMS":
      state.questions = arrayMove(
        state.questions,
        action.oldIndex,
        action.newIndex,
      );
      break;
    case "REMOVE_ITEM_AT_INDEX":
      state.questions.splice(action.index, 1);
      break;
    case "REMOVE_ITEMS_AT_INDEX": {
      const next: Resource[] = [];
      for (let i = 0; i < state.questions.length; i++) {
        if (!action.indexes.includes(i)) {
          next.push(state.questions[i]);
        }
      }
      state.questions = next;
      break;
    }
    case "TOGGLE_OBJECTIVE": {
      const index = state.objectives.findIndex(
        (o) => o.id === action.objective.id,
      );
      if (index !== -1) {
        state.objectives.splice(index, 1);
      } else {
        state.objectives.push(action.objective);
      }
      break;
    }
    case "ADD_OBJECTIVES": {
      for (const objective of action.objectives) {
        if (!state.objectives.find((o) => o.id === objective.id)) {
          state.objectives.push(objective);
        }
      }
      break;
    }
    case "REMOVE_OBJECTIVES": {
      for (const objective of action.objectives) {
        const index = state.objectives.findIndex((o) => o.id === objective.id);
        if (index !== -1) {
          state.objectives.splice(index, 1);
        }
      }
      break;
    }
    case "CLEAR_OBJECTIVES":
      state.objectives = [];
      break;
  }
};
